import React, { Component, type ErrorInfo, type ReactNode } from "react";

import { WorkspaceErrorFallback } from "./WorkspaceErrorFallback";

type WorkspaceErrorBoundaryProps = {
  children: ReactNode;
  onReset?: () => void;
};

type WorkspaceErrorBoundaryState = {
  hasError: boolean;
  message: string | null;
};

export class WorkspaceErrorBoundary extends Component<
  WorkspaceErrorBoundaryProps,
  WorkspaceErrorBoundaryState
> {
  state: WorkspaceErrorBoundaryState = {
    hasError: false,
    message: null,
  };

  static getDerivedStateFromError(error: Error): WorkspaceErrorBoundaryState {
    return {
      hasError: true,
      message: error.message || "Unexpected workspace error",
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("WorkspaceErrorBoundary caught an error:", error, errorInfo);
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, message: null });
    this.props.onReset?.();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <WorkspaceErrorFallback
          description={this.state.message ?? undefined}
          onRetry={this.handleRetry}
        />
      );
    }
    return this.props.children;
  }
}
